import React from "react";
import {
  createRouter,
  createRoute,
  createRootRoute,
  Outlet,
  redirect,
} from "@tanstack/react-router";

import { DashboardLayout } from "./components/DashboardLayout";
import { SuperAdminLayout } from "./components/SuperAdminLayout";
import { AffiliatePortalLayout, AdvertiserPortalLayout } from "./components/PortalLayout";
import { DashboardPage } from "./features/dashboard/DashboardPage";
import { OffersPage } from "./features/offers/OffersPage";
import { OfferDetailPage } from "./features/offers/OfferDetailPage";
import { OfferCreatePage } from "./features/offers/OfferCreatePage";
import { OfferGroupsPage } from "./features/offers/OfferGroupsPage";
import { OfferGroupDetailPage } from "./features/offers/OfferGroupDetailPage";
import { OfferApplicationsPage } from "./features/offers/OfferApplicationsPage";
import { TrafficControlsPage } from "./features/traffic-controls/TrafficControlsPage";
import { AffiliatesPage } from "./features/affiliates/AffiliatesPage";
import { AffiliateDetailPage } from "./features/affiliates/AffiliateDetailPage";
import { AffiliateCreatePage } from "./features/affiliates/AffiliateCreatePage";
import { TiersPage } from "./features/affiliates/TiersPage";
import { TierDetailPage } from "./features/affiliates/TierDetailPage";
import { AdvertisersPage } from "./features/advertisers/AdvertisersPage";
import { AdvertiserDetailPage } from "./features/advertisers/AdvertiserDetailPage";
import { AdvertiserCreatePage } from "./features/advertisers/AdvertiserCreatePage";
import { ReportingPage } from "./features/reporting/ReportingPage";
import { RawReportPage } from "./features/reporting/RawReportPage";
import { CsvImportPage } from "./features/reporting/CsvImportPage";
import { LabelsPage } from "./features/settings/LabelsPage";
import { CategoriesPage } from "./features/settings/CategoriesPage";
import { UsersPage } from "./features/settings/UsersPage";
import { SettingsPage } from "./features/settings/SettingsPage";
import { SmartLinksPage } from "./features/smart-links/SmartLinksPage";
import { SuperAdminOverview } from "./features/super-admin/SuperAdminOverview";
import { TenantsPage } from "./features/super-admin/TenantsPage";
import { TenantDetailPage } from "./features/super-admin/TenantDetailPage";

// Affiliate portal pages
import { AffDashboardPage } from "./features/portal/affiliate/AffDashboardPage";
import { AffOffersPage } from "./features/portal/affiliate/AffOffersPage";
import { AffOfferDetailPage } from "./features/portal/affiliate/AffOfferDetailPage";
import { AffReportingClicksPage } from "./features/portal/affiliate/AffReportingClicksPage";
import { AffReportingConversionsPage } from "./features/portal/affiliate/AffReportingConversionsPage";
import { AffPostbacksPage } from "./features/portal/affiliate/AffPostbacksPage";
import { AffAccountPage } from "./features/portal/affiliate/AffAccountPage";

// Advertiser portal pages
import { AdvDashboardPage } from "./features/portal/advertiser/AdvDashboardPage";
import { AdvOffersPage } from "./features/portal/advertiser/AdvOffersPage";
import { AdvOfferDetailPage } from "./features/portal/advertiser/AdvOfferDetailPage";
import { AdvReportingClicksPage } from "./features/portal/advertiser/AdvReportingClicksPage";
import { AdvReportingConversionsPage } from "./features/portal/advertiser/AdvReportingConversionsPage";
import { AdvAccountPage } from "./features/portal/advertiser/AdvAccountPage";

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

const layoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "layout",
  component: function LayoutRoute() {
    return (
      <DashboardLayout>
        <Outlet />
      </DashboardLayout>
    );
  },
});

const indexRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/",
  component: DashboardPage,
});

const offersRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/offers",
  component: OffersPage,
});

const offerDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/offers/$id",
  component: OfferDetailPage,
});

const offerCreateRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/offers/new",
  component: OfferCreatePage,
});

const affiliatesRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/affiliates",
  component: AffiliatesPage,
});

const affiliateDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/affiliates/$id",
  component: AffiliateDetailPage,
});

const affiliateCreateRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/affiliates/new",
  component: AffiliateCreatePage,
});

const advertisersRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/advertisers",
  component: AdvertisersPage,
});

const advertiserDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/advertisers/$id",
  component: AdvertiserDetailPage,
});

const advertiserCreateRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/advertisers/new",
  component: AdvertiserCreatePage,
});

const offerGroupsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/offer-groups",
  component: OfferGroupsPage,
});

const offerGroupDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/offer-groups/$id",
  component: OfferGroupDetailPage,
});

const offerApplicationsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/applications",
  component: OfferApplicationsPage,
});

const trafficControlsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/traffic-controls",
  component: TrafficControlsPage,
});

const tiersRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/tiers",
  component: TiersPage,
});

const tierDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/tiers/$id",
  component: TierDetailPage,
});

const smartLinksRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/smart-links",
  component: SmartLinksPage,
});

const reportingRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/reporting",
  component: ReportingPage,
});

const rawReportRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/reporting/raw",
  component: RawReportPage,
});

const csvImportRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/reporting/import",
  component: CsvImportPage,
});

const labelsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/settings/labels",
  component: LabelsPage,
});

const categoriesRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/settings/categories",
  component: CategoriesPage,
});

const settingsUsersRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/settings/users",
  component: UsersPage,
});

const settingsIndexRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/settings",
  component: SettingsPage,
});

// Super admin layout
const superAdminLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "super-admin-layout",
  component: function SALayoutRoute() {
    return (
      <SuperAdminLayout>
        <Outlet />
      </SuperAdminLayout>
    );
  },
});

const superAdminIndexRoute = createRoute({
  getParentRoute: () => superAdminLayoutRoute,
  path: "/super-admin",
  component: SuperAdminOverview,
});

const superAdminTenantsRoute = createRoute({
  getParentRoute: () => superAdminLayoutRoute,
  path: "/super-admin/tenants",
  component: TenantsPage,
});

const superAdminTenantDetailRoute = createRoute({
  getParentRoute: () => superAdminLayoutRoute,
  path: "/super-admin/tenants/$id",
  component: TenantDetailPage,
});

// Affiliate portal layout
const affPortalLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "aff-portal-layout",
  component: function AffPortalLayoutRoute() {
    return (
      <AffiliatePortalLayout>
        <Outlet />
      </AffiliatePortalLayout>
    );
  },
});

const affPortalIndex = createRoute({ getParentRoute: () => affPortalLayoutRoute, path: "/portal/affiliate", component: AffDashboardPage });
const affPortalOffers = createRoute({ getParentRoute: () => affPortalLayoutRoute, path: "/portal/affiliate/offers", component: AffOffersPage });
const affPortalOfferDetail = createRoute({ getParentRoute: () => affPortalLayoutRoute, path: "/portal/affiliate/offers/$offerId", component: AffOfferDetailPage });
const affPortalClickReport = createRoute({ getParentRoute: () => affPortalLayoutRoute, path: "/portal/affiliate/reporting/clicks", component: AffReportingClicksPage });
const affPortalConvReport = createRoute({ getParentRoute: () => affPortalLayoutRoute, path: "/portal/affiliate/reporting/conversions", component: AffReportingConversionsPage });
const affPortalPostbacks = createRoute({ getParentRoute: () => affPortalLayoutRoute, path: "/portal/affiliate/postbacks", component: AffPostbacksPage });
const affPortalAccount = createRoute({ getParentRoute: () => affPortalLayoutRoute, path: "/portal/affiliate/account", component: AffAccountPage });

// Advertiser portal layout
const advPortalLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "adv-portal-layout",
  component: function AdvPortalLayoutRoute() {
    return (
      <AdvertiserPortalLayout>
        <Outlet />
      </AdvertiserPortalLayout>
    );
  },
});

const advPortalIndex = createRoute({ getParentRoute: () => advPortalLayoutRoute, path: "/portal/advertiser", component: AdvDashboardPage });
const advPortalOffers = createRoute({ getParentRoute: () => advPortalLayoutRoute, path: "/portal/advertiser/offers", component: AdvOffersPage });
const advPortalOfferDetail = createRoute({ getParentRoute: () => advPortalLayoutRoute, path: "/portal/advertiser/offers/$offerId", component: AdvOfferDetailPage });
const advPortalClickReport = createRoute({ getParentRoute: () => advPortalLayoutRoute, path: "/portal/advertiser/reporting/clicks", component: AdvReportingClicksPage });
const advPortalConvReport = createRoute({ getParentRoute: () => advPortalLayoutRoute, path: "/portal/advertiser/reporting/conversions", component: AdvReportingConversionsPage });
const advPortalAccount = createRoute({ getParentRoute: () => advPortalLayoutRoute, path: "/portal/advertiser/account", component: AdvAccountPage });

const routeTree = rootRoute.addChildren([
  layoutRoute.addChildren([
    indexRoute,
    offerCreateRoute,
    offersRoute,
    offerDetailRoute,
    offerGroupsRoute,
    offerGroupDetailRoute,
    offerApplicationsRoute,
    trafficControlsRoute,
    affiliateCreateRoute,
    affiliatesRoute,
    affiliateDetailRoute,
    tiersRoute,
    tierDetailRoute,
    advertiserCreateRoute,
    advertisersRoute,
    advertiserDetailRoute,
    smartLinksRoute,
    reportingRoute,
    rawReportRoute,
    csvImportRoute,
    labelsRoute,
    categoriesRoute,
    settingsUsersRoute,
    settingsIndexRoute,
  ]),
  superAdminLayoutRoute.addChildren([
    superAdminIndexRoute,
    superAdminTenantsRoute,
    superAdminTenantDetailRoute,
  ]),
  affPortalLayoutRoute.addChildren([
    affPortalIndex,
    affPortalOffers,
    affPortalOfferDetail,
    affPortalClickReport,
    affPortalConvReport,
    affPortalPostbacks,
    affPortalAccount,
  ]),
  advPortalLayoutRoute.addChildren([
    advPortalIndex,
    advPortalOffers,
    advPortalOfferDetail,
    advPortalClickReport,
    advPortalConvReport,
    advPortalAccount,
  ]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
